import { z } from 'zod';

import { TASK_STATUSES, TASK_URGENCIES, type TaskStatus, type TaskUrgency } from '../domain.js';
import { dateOnlySchema, idSchema, optionalText } from './common.js';

/*
 * Tarefas — ADR 063.
 *
 * Uma tarefa tem título, descrição, situação, prioridade, prazo, as pessoas que
 * participam e uma lista de itens para marcar. Sem quadro e sem coluna: a
 * situação é o lugar da tarefa, e a lista se arruma por ela.
 */

const tituloSchema = z
  .string()
  .trim()
  .min(2, 'Dê um título à tarefa')
  .max(200, 'No máximo 200 caracteres');

const itemSchema = z.string().trim().min(1, 'Escreva o item').max(300, 'No máximo 300 caracteres');

/** Os limites da tarefa: quantas pessoas e quantos itens cabem nela. */
export const TASK_MAX_MEMBERS = 30;
export const TASK_MAX_CHECKLIST_ITEMS = 100;

export const createTaskBodySchema = z.object({
  title: tituloSchema,
  description: optionalText(4000),
  /** Ausente, a tarefa nasce "a fazer". */
  status: z.enum(TASK_STATUSES).optional(),
  /** Ausente, prioridade normal. */
  urgency: z.enum(TASK_URGENCIES).optional(),
  dueAt: dateOnlySchema.optional(),
  /** Quem participa, além de quem cria — que entra sempre. */
  memberIds: z
    .array(idSchema)
    .max(TASK_MAX_MEMBERS, `No máximo ${TASK_MAX_MEMBERS} pessoas na tarefa`)
    .optional(),
  /** Os itens do checklist, na ordem em que foram escritos. */
  checklist: z
    .array(itemSchema)
    .max(TASK_MAX_CHECKLIST_ITEMS, `No máximo ${TASK_MAX_CHECKLIST_ITEMS} itens`)
    .optional(),
});
export type CreateTaskBody = z.infer<typeof createTaskBodySchema>;

/**
 * A edição: cada campo vem sozinho, quando muda — a tela grava na hora, campo a
 * campo. Descrição vazia e prazo nulo apagam o que havia.
 */
export const updateTaskBodySchema = z
  .object({
    title: tituloSchema.optional(),
    description: z
      .string()
      .trim()
      .max(4000, 'No máximo 4000 caracteres')
      .transform((valor) => (valor === '' ? null : valor))
      .nullable()
      .optional(),
    status: z.enum(TASK_STATUSES).optional(),
    urgency: z.enum(TASK_URGENCIES).optional(),
    dueAt: dateOnlySchema.nullable().optional(),
  })
  .refine((corpo) => Object.values(corpo).some((valor) => valor !== undefined), {
    message: 'Nada a alterar',
  });
export type UpdateTaskBody = z.infer<typeof updateTaskBodySchema>;

export const taskMemberBodySchema = z.object({ userId: idSchema });
export type TaskMemberBody = z.infer<typeof taskMemberBodySchema>;

export const createChecklistItemBodySchema = z.object({ content: itemSchema });
export type CreateChecklistItemBody = z.infer<typeof createChecklistItemBodySchema>;

export const updateChecklistItemBodySchema = z
  .object({
    content: itemSchema.optional(),
    isDone: z.boolean().optional(),
  })
  .refine((corpo) => corpo.content !== undefined || corpo.isDone !== undefined, {
    message: 'Nada a alterar',
  });
export type UpdateChecklistItemBody = z.infer<typeof updateChecklistItemBodySchema>;

/** Comentário: texto simples, de um caractere a 4000. */
export const taskCommentBodySchema = z.object({
  content: z.string().trim().min(1, 'Escreva o comentário').max(4000, 'No máximo 4000 caracteres'),
});
export type TaskCommentBody = z.infer<typeof taskCommentBodySchema>;

// ─────────────────────────────────────────────────────── respostas

export interface TaskPerson {
  readonly id: string;
  readonly name: string;
  readonly avatarUrl: string | null;
}

export interface TaskChecklistItem {
  readonly id: string;
  readonly content: string;
  readonly isDone: boolean;
  readonly position: number;
  /** Quem marcou, e quando: o registro de quem fez. */
  readonly doneBy: TaskPerson | null;
  readonly doneAt: string | null;
}

export interface Task {
  readonly id: string;
  readonly title: string;
  readonly description: string | null;
  readonly status: TaskStatus;
  readonly urgency: TaskUrgency;
  readonly dueAt: string | null;
  readonly createdBy: TaskPerson;
  /** Quem participa, com quem criou sempre em primeiro. */
  readonly members: readonly TaskPerson[];
  readonly checklist: readonly TaskChecklistItem[];
  /** Comentários vivos; os comentários em si vêm à parte, ao abrir a tarefa. */
  readonly commentCount: number;
  readonly completedAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  /** Quem olha criou a tarefa — e só quem criou a exclui. */
  readonly canDelete: boolean;
}

export interface TaskListResult {
  readonly items: readonly Task[];
}

export interface TaskComment {
  readonly id: string;
  readonly author: TaskPerson;
  readonly content: string;
  readonly createdAt: string;
  /** Quando foi reescrito; nulo se nunca foi. */
  readonly editedAt: string | null;
  /** Quem olha escreveu este comentário — e só quem escreveu o reescreve ou apaga. */
  readonly canEdit: boolean;
}

export interface TaskCommentListResult {
  /** Em ordem de chegada: o mais antigo primeiro. */
  readonly items: readonly TaskComment[];
}
